/**
 * Assessments page copy — from the owner's "Assessments page v2" brief
 * (2026-09-15). Public prose on this page is colon-led: no em dashes, no
 * Oxford comma. Nothing here names a laboratory code, a component analyte, a
 * tube type or a wholesale price, and nothing promises a collection method
 * or a turnaround time: the brief holds both until the Inuvi pathway is
 * confirmed in contract.
 *
 * The brief supplies seven categories in full; Genetics and Monitoring come
 * from the owner's own note on the same day. Approved to publish by the owner
 * on 2026-09-15.
 */

export const ASSESSMENTS_META = {
  title: 'Assessments · Melio',
  description:
    'Personalised tests chosen for a reason. Explore a broad range of health profiles, request a test that matters to you or let your clinician recommend what could add meaningful context to your care.',
} as const;

export const ASSESSMENTS_INTRO = {
  eyebrow: 'Assessments',
  heading: 'Personalised tests chosen for a reason.',
  body:
    'Explore a broad range of health profiles, request a test that matters to you or let your clinician recommend what could add meaningful context to your care.',
} as const;

export const ASSESSMENTS_SECTIONS = {
  paths: { numeral: 'I', title: 'Two paths' },
  library: { numeral: 'II', title: 'Profile library' },
  bespoke: { numeral: 'III', title: 'Bespoke profiles' },
  process: { numeral: 'IV', title: 'How selection works' },
} as const;

export const TWO_PATHS = {
  framing:
    'Your membership with Melio starts with your history, priorities and previous results, rather than a fixed basket of tests. You can explore specific profiles and request testing that matters to you, while your clinician may also recommend focused testing where it could add useful context to your care.',
  paths: [
    {
      id: 'clinician-guided',
      title: 'Clinician guided',
      body:
        'Your clinician may recommend a routine profile, a more focused health profile or a bespoke combination in response to your history, symptoms, previous results or emerging findings.',
    },
    {
      id: 'member-requested',
      title: 'Member requested',
      body:
        'Browse the profile library and ask your Melio team about any area or specific test you would like to explore as an add-on.',
    },
  ],
  qualifier:
    'Member-requested and clinician-recommended testing is subject to clinical suitability and service availability. Sample requirements, turnaround times and fees vary by profile.',
} as const;

export interface AssessmentProfile {
  name: string;
  /** One sentence. Never analytes, codes, tubes or prices. */
  summary: string;
}

export interface AssessmentCategory {
  id: string;
  title: string;
  description: string;
  profiles: AssessmentProfile[];
}

export const ASSESSMENT_CATEGORIES: AssessmentCategory[] = [
  {
    id: 'routine',
    title: 'Routine profiles',
    description:
      'Focused checks for common areas of health, from blood counts and blood sugar to liver, kidney, iron and cardiovascular markers.',
    profiles: [
      { name: 'Bone Profile', summary: 'A focused view of markers associated with bone health, mineral balance and inflammation.' },
      { name: 'Calcium Check', summary: 'A targeted check of calcium status, including an adjustment that helps interpretation alongside blood protein levels.' },
      { name: 'Diabetes Screen', summary: 'A snapshot of recent and longer-term blood sugar regulation to identify results that may warrant further clinical review.' },
      { name: 'Full Blood Count', summary: 'An encompassing view of red cells, white cells and platelets that can help investigate concerns such as anaemia, infection or inflammation.' },
      { name: 'HbA1c', summary: 'A measure of average blood sugar over the preceding two to three months.' },
      { name: 'Iron Profile', summary: 'A detailed view of circulating iron and iron stores to help assess possible deficiency or overload.' },
      { name: 'Iron Profile Plus', summary: 'An extended iron assessment that adds nutritional context relevant to healthy blood-cell production.' },
      { name: 'Kidney Function Profile', summary: 'A focused assessment of how effectively the kidneys are filtering waste from the blood.' },
      { name: 'Lipid Profile', summary: 'A detailed view of blood fats used to inform cardiovascular risk assessment.' },
      { name: 'Cardiovascular Screen', summary: 'A combined view of cholesterol, longer-term blood sugar and inflammation-related cardiovascular risk markers.' },
      { name: 'Liver Function Test', summary: 'A group of markers that helps assess liver function and look for patterns that may merit further review.' },
      { name: 'Urine Microscopy Check', summary: 'A laboratory examination of urine for cells, crystals and other findings that may support investigation of urinary or kidney concerns.' },
    ],
  },
  {
    id: 'biochemistry',
    title: 'Biochemistry profiles',
    description:
      'Combined profiles that bring together several areas of organ and metabolic health, ranging from concise checks to broader baseline reviews.',
    profiles: [
      { name: 'Comprehensive Biochemistry Profile', summary: 'A broad baseline spanning metabolic health, inflammation, iron status and major organ systems.' },
      { name: 'Essential Biochemistry Profile', summary: 'A streamlined overview of metabolic, iron, liver, kidney and cardiovascular health.' },
      { name: 'Metabolic and Muscle Profile', summary: 'An extended assessment of metabolic and organ health with additional context on muscle turnover and cellular stress.' },
      { name: 'Blood, Liver and Kidney Profile', summary: 'A focused combined view of blood cells, liver function, kidney filtration and electrolyte balance.' },
      { name: 'Nutritional Biochemistry Profile', summary: 'A broad profile connecting blood-cell health, key vitamins and liver and kidney function.' },
      { name: 'Organ Health Profile', summary: 'A concise combined assessment of kidney, liver and lipid health.' },
      { name: 'Liver and Lipid Profile', summary: 'A focused check of liver markers and blood fats.' },
      { name: 'Extended Metabolic Profile', summary: 'A wide baseline covering blood cells, blood sugar, inflammation, iron and major organ systems.' },
      { name: 'Core Metabolic Profile', summary: 'A practical general-health review of blood cells, blood sugar, lipids and liver and kidney function.' },
    ],
  },
  {
    id: 'hormones',
    title: 'Hormone profiles',
    description:
      'Focused combinations used to explore reproductive, pituitary and androgen hormone patterns in the context of symptoms, life stage and clinical history.',
    profiles: [
      { name: 'Cycle Hormone Profile', summary: 'A focused view of key hormones involved in ovulation and the menstrual cycle.' },
      { name: 'Extended Sex Hormone Profile', summary: 'A broader assessment of reproductive and androgen hormones for a more complete view of hormonal balance.' },
      { name: 'Androgen Balance Profile', summary: 'A detailed look at testosterone availability and the pituitary and reproductive hormones that influence it.' },
      { name: 'Reproductive Reserve and Hormone Profile', summary: 'An extended reproductive assessment that combines ovarian-reserve, cycle and androgen context.' },
      { name: 'Steroid Hormone Profile', summary: 'A concise view of selected adrenal and sex hormones that may help explore hormonal symptoms.' },
      { name: 'Fertility Hormone Profile', summary: 'A broad reproductive-hormone review used to support clinically directed fertility assessment.' },
      { name: 'Pituitary and Androgen Profile', summary: 'A focused assessment of pituitary signalling and androgen status.' },
      { name: 'Gonadal and Androgen Profile', summary: 'A combined view of pituitary, adrenal and androgen hormones involved in reproductive function.' },
      { name: 'Oestradiol and Testosterone Profile', summary: 'A concise comparison of two key sex hormones where a focused check is clinically appropriate.' },
    ],
  },
  {
    id: 'thyroid',
    title: 'Thyroid profiles',
    description:
      'Profiles ranging from a first-line thyroid check to broader assessments of thyroid hormones, autoimmunity and related health context.',
    profiles: [
      { name: 'Basic Thyroid Function Profile', summary: 'A first-line assessment of thyroid activity and the hormones responsible for regulating it.' },
      { name: 'Advanced Thyroid Profile', summary: 'A broader thyroid assessment that adds markers associated with thyroid autoimmunity.' },
      { name: 'Advanced Thyroid Health Profile', summary: 'An extended thyroid review with additional nutritional and inflammation context.' },
      { name: 'Advanced Thyroid and Hormone Profile', summary: 'A combined assessment for situations where thyroid and reproductive or androgen symptoms may overlap.' },
      { name: 'Advanced Thyroid Profile with Reverse T3', summary: 'A specialist thyroid profile that includes reverse T3 and is best reserved for a clear clinical indication.' },
    ],
  },
  {
    id: 'vitamins',
    title: 'Vitamin profiles',
    description:
      'Focused checks for common vitamin deficiencies, with broader options that add iron, inflammation and metabolic context.',
    profiles: [
      { name: 'Vitamin Profile 1', summary: 'A concise check of vitamin B12 and vitamin D status.' },
      { name: 'Vitamin Profile 2', summary: 'A broader vitamin review covering active B12, folate and vitamin D.' },
      { name: 'Vitamin Profile 3', summary: 'A combined vitamin and iron assessment relevant to energy, blood-cell production and nutritional status.' },
      { name: 'Vitamin Profile 4', summary: 'An extended nutritional profile with additional context on inflammation, iron stores, magnesium and cardiovascular health.' },
    ],
  },
  {
    id: 'womens-health',
    title: "Women's health",
    description:
      'Clinician-directed profiles for general health, menstrual and hormonal concerns, fertility, menopause and polycystic ovary syndrome.',
    profiles: [
      { name: 'Well Woman Check', summary: 'A broad general-health assessment spanning blood, metabolic, organ, nutritional, thyroid and reproductive-hormone health.' },
      { name: 'Advanced Well Woman Check', summary: 'An extended baseline combining broad general-health markers with active vitamin and reproductive-hormone context.' },
      { name: 'Female Hormone Profile', summary: 'A detailed view of reproductive, thyroid and androgen hormones used to explore menstrual or hormonal concerns.' },
      { name: 'Female Hormone Profile Plus 1', summary: 'An extended hormone review with adrenal, androgen, cycle and vitamin D context.' },
      { name: 'Female Hormone Profile Plus 2', summary: 'A wider cycle-hormone assessment that adds pituitary signalling to the extended hormone review.' },
      { name: 'Fertility Hormone Profile 1', summary: 'A comprehensive fertility-oriented hormone assessment including ovarian-reserve, pituitary and thyroid context.' },
      { name: 'Fertility Hormone Profile 2', summary: 'A focused fertility check combining ovarian-reserve, prolactin and thyroid markers.' },
      { name: 'Fertility Hormone Profile 3', summary: 'An extended ovarian-reserve profile with additional thyroid and vitamin D context.' },
      { name: 'Menopause Profile', summary: 'A focused hormone assessment that can support clinical review of perimenopausal or menopausal symptoms.' },
      { name: 'Ovarian Reserve Test', summary: 'A group of reproductive-hormone markers used to add context to ovarian reserve and fertility discussions.' },
      { name: 'PCOS Check', summary: 'A focused assessment of pituitary and androgen patterns commonly reviewed when polycystic ovary syndrome is suspected.' },
      { name: 'PCOS Plus', summary: 'A broader PCOS-oriented assessment that adds adrenal, thyroid and metabolic context to reproductive hormones.' },
    ],
  },
  {
    id: 'mens-health',
    title: "Men's health",
    description:
      'Clinician-directed profiles for general health, hormones, prostate context, sexual health concerns and testosterone treatment monitoring.',
    profiles: [
      { name: 'Erectile Dysfunction Profile', summary: 'A broad assessment of hormonal, metabolic, thyroid and cardiovascular factors that may contribute to erectile difficulties.' },
      { name: 'Male Hormone Profile', summary: 'A detailed review of pituitary, adrenal and androgen hormones affecting male hormonal health.' },
      { name: 'Male Hormone Profile Plus', summary: 'An extended hormone profile with additional blood, prostate and vitamin D context.' },
      { name: 'PSA Test', summary: 'A prostate-specific blood test that requires age, symptoms, recent activity and clinical history to be considered when interpreting the result.' },
      { name: 'PSA Plus Profile', summary: 'A broader prostate-oriented check with additional thyroid and liver context.' },
      { name: 'TRT Monitoring Profile', summary: 'A profile designed to support clinician-led monitoring of testosterone replacement therapy and related safety markers.' },
      { name: 'Well Man Check', summary: 'A broad general-health assessment spanning blood, metabolic, organ, thyroid, nutritional and hormonal health.' },
      { name: 'Well Man Check 40+', summary: 'An extended general-health review for midlife that adds prostate context to a broad baseline.' },
    ],
  },
  {
    id: 'genetics',
    title: 'Genetics',
    description:
      'Inherited context read alongside your history: a baseline genetics kit at the start of membership, with focused genetic add-ons where they could inform your care.',
    profiles: [
      { name: 'Baseline Genetics Kit', summary: 'A lab-direct saliva kit that gives your clinician inherited context for the wider picture, rather than a verdict on its own.' },
      { name: 'Pharmacogenomics', summary: 'How your genes may influence the way you respond to certain medicines, used to inform prescribing decisions with your clinician.' },
      { name: 'Polygenic Risk Scoring', summary: 'An estimate of inherited predisposition across common conditions, interpreted with your history and results rather than in isolation.' },
    ],
  },
  {
    id: 'monitoring',
    title: 'Monitoring',
    description:
      'Measures that matter as a trend rather than a single reading, tracked between reviews and read in context.',
    profiles: [
      { name: 'Blood Pressure Monitoring', summary: 'Home readings over a period rather than one clinic measurement, reviewed by your clinician as part of your record.' },
    ],
  },
];

export const BESPOKE_PROFILES = {
  paragraphs: [
    'Where a standard profile does not fit the question, a focused combination can be shaped around it. Your clinician chooses the relevant markers, arranges one collection and reads the results as a single report in the context of your record.',
    'Bespoke profiles are clinician-led: they are proposed when a particular question makes them useful, explained before anything is arranged and priced individually.',
  ],
} as const;

export const SELECTION_STEPS = [
  {
    id: 'explore',
    numeral: 'I',
    title: 'Explore what matters to you',
    body: 'Browse the profile library and ask your Melio team about any area or specific test you would like to explore as an add-on.',
  },
  {
    id: 'whole-picture',
    numeral: 'II',
    title: 'Start with the whole picture',
    body: 'We review your health history, family history, priorities, symptoms and any useful previous results before recommending new testing.',
  },
  {
    id: 'guidance',
    numeral: 'III',
    title: 'Add clinical guidance',
    body: 'Your clinician can recommend a routine profile, a more focused health profile or a bespoke combination according to what is relevant for you.',
  },
  {
    id: 'context',
    numeral: 'IV',
    title: 'Review results in context',
    body: 'Results are reviewed alongside the rest of your health record, explained clearly and used to shape appropriate next steps.',
  },
];

export const ASSESSMENTS_CTA = {
  title: 'Discuss an assessment.',
  body: 'Tell us the question you want answered and your clinician will say what could add useful context: a profile, a bespoke combination or nothing new for now.',
  action: { label: 'Discuss an assessment' },
} as const;

